import React, { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';
import clsx from 'clsx';
import {
  Menu,
  MenuButton,
  MenuButtonProps,
  MenuItem,
  MenuItemProps,
  MenuItems,
  MenuItemsProps,
  MenuLink,
  MenuLinkProps,
  MenuList,
  MenuListProps,
  MenuPopover,
  MenuPopoverProps,
  MenuProps
} from '@reach/menu-button';

import ActionsArrowDown from '@strava/icons/ActionsArrowDownNormalXsmall';

import '@reach/menu-button/styles.css';
import buttonStyles from '../Button/Button.module.scss';
import styles from './Dropdown.module.scss';

interface BaseProps {
  className?: string;
  children: ReactNode;
}

//
// Menu
//
// Reach UI docs: https://reach.tech/menu-button/#menu
// --------------------
export function DropdownMenu({
  children,
  ...otherProps
}: BaseProps & MenuProps) {
  return <Menu {...otherProps}>{children}</Menu>;
}

// Override Menu display name so that error from prop types show 'DropdownMenu'
Menu.displayName = 'DropdownMenu';

//
// MenuButton
//
// Reach UI docs: https://reach.tech/menu-button/#menubutton
// --------------------

export function DropdownButton({
  className,
  children,
  onClick,
  ...otherProps
}: BaseProps & ComponentPropsWithoutRef<'button'> & MenuButtonProps) {
  return (
    <MenuButton
      className={clsx(
        buttonStyles.btn,
        buttonStyles.default,
        styles.btn,
        className
      )}
      onClick={onClick}
      {...otherProps}
    >
      {children}
    </MenuButton>
  );
}

// Override MenuButton display name so that error from prop types show 'DropdownButton'
MenuButton.displayName = 'DropdownButton';

//
// MenuList
//
// Reach UI docs: https://reach.tech/menu-button/#menulist
// --------------------

export function DropdownList({
  className,
  children,
  ...otherProps
}: BaseProps & ComponentPropsWithoutRef<'div'> & MenuListProps) {
  return (
    <MenuList className={clsx(styles.list, className)} {...otherProps}>
      {children}
    </MenuList>
  );
}

// Override Menu display name so that error from prop types show 'DropdownList'
MenuList.displayName = 'DropdownList';

//
// MenuPopover
//
// Reach UI docs: https://reach.tech/menu-button/#menupopover
// --------------------

export function DropdownPopover({
  className,
  children,
  portal,
  ...otherProps
}: BaseProps & ComponentPropsWithoutRef<'div'> & MenuPopoverProps) {
  return (
    <MenuPopover
      className={clsx(styles.list, className)}
      portal={portal}
      {...otherProps}
    >
      {children}
    </MenuPopover>
  );
}

// Override Menu display name so that error from prop types show 'DropdownPopover'
Menu.displayName = 'DropdownPopover';

//
// MenuItems
//
// Reach UI docs: https://reach.tech/menu-button/#menuitems
// --------------------

export function DropdownItems({
  className,
  children,
  ...otherProps
}: BaseProps & ComponentPropsWithoutRef<'div'> & MenuItemsProps) {
  return (
    <MenuItems className={clsx(styles.list, className)} {...otherProps}>
      {children}
    </MenuItems>
  );
}

// Override Menu display name so that error from prop types show 'DropdownItems'
MenuItems.displayName = 'DropdownItems';

//
// DropdownLink
//
// Reach UI docs: https://reach.tech/menu-button/#menulink
// --------------------

interface DropdownLinkProps extends BaseProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  as?: any;
}

type ElementProps<Element extends ElementType> = DropdownLinkProps &
  ComponentPropsWithoutRef<Element> &
  MenuLinkProps;

export const DropdownLink = <Element extends ElementType = 'a'>({
  as,
  href,
  children,
  className,
  ...otherProps
}: ElementProps<Element>) => {
  return (
    <MenuLink
      as={as}
      className={clsx(styles.item, className)}
      href={href}
      {...otherProps}
    >
      {children}
    </MenuLink>
  );
};

// Override Menu display name so that error from prop types show 'DropdownLink'
MenuLink.displayName = 'DropdownLink';

//
// DropdownItem
//
// Reach UI docs: https://reach.tech/menu-button/#menuitem
// --------------------

interface DropdownItemProps extends BaseProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  as?: any;
}

export function DropdownItem({
  as,
  onSelect,
  children,
  className,
  ...otherProps
}: DropdownItemProps & MenuItemProps & ComponentPropsWithoutRef<'div'>) {
  return (
    <MenuItem
      as={as}
      className={clsx(styles.item, className)}
      onSelect={onSelect}
      {...otherProps}
    >
      {children}
    </MenuItem>
  );
}

// Override Menu display name so that error from prop types show 'DropdownItem'
MenuItem.displayName = 'DropdownItem';

//
// Dropdown
// --------------------

interface DropdownProps extends ComponentPropsWithoutRef<'button'> {
  text: string;
  buttonClassName?: string;
}

export function Dropdown({
  text,
  children,
  onClick = () => {},
  className,
  buttonClassName,
  ...props
}: DropdownProps) {
  return (
    <DropdownMenu>
      <DropdownButton
        className={buttonClassName || className}
        onClick={onClick}
        {...props}
      >
        {text}
        <ActionsArrowDown className={styles.icon} />
      </DropdownButton>
      <DropdownList className={className} portal={false} hidden={false}>
        {children}
      </DropdownList>
    </DropdownMenu>
  );
}
